import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import {
    HOME_KPI_STATS_URL,
    buildStatsParams,
    canViewKpiType,
} from "@/utils/home-kpi-stats";

const state = {
    definitions: [],
    definitions_loading: false,
    definitions_errors: null,

    values: {},
    values_loading: {},

    preview: {
        loading: false,
        data: null,
        error: null,
    },
    preview_cancel_source: null,

    saving: false,
    preferences_saving: false,
};

const mutations = {
    updateDefinitions(state, definitions) {
        state.definitions = definitions || [];
    },
    updateDefinitionsLoading(state, loading) {
        state.definitions_loading = loading;
    },
    updateDefinitionsErrors(state, errors) {
        state.definitions_errors = errors;
    },
    updateValue(state, { id, data, loading, error }) {
        if (loading !== undefined) {
            state.values_loading = { ...state.values_loading, [id]: loading };
        }
        if (data !== undefined || error !== undefined) {
            state.values = {
                ...state.values,
                [id]: { data: data ?? state.values[id]?.data ?? null, error: error ?? null },
            };
        }
    },
    updatePreview(state, payload) {
        state.preview = { ...state.preview, ...payload };
    },
    updatePreviewCancelSource(state, source) {
        state.preview_cancel_source = source;
    },
    updateSaving(state, saving) {
        state.saving = saving;
    },
    updatePreferencesSaving(state, saving) {
        state.preferences_saving = saving;
    },
    applyUserPreferences(state, preferences) {
        const prefById = new Map(
            (preferences || []).map((pref) => [pref.kpi_definition_id, pref])
        );
        state.definitions = (state.definitions || []).map((defn) => {
            const pref = prefById.get(defn.id);
            if (!pref) {
                return defn;
            }
            return {
                ...defn,
                user_preference: {
                    ...(defn.user_preference || {}),
                    visible:
                        pref.visible !== undefined
                            ? pref.visible
                            : defn.user_preference?.visible,
                    sort_order:
                        pref.sort_order !== undefined
                            ? pref.sort_order
                            : defn.user_preference?.sort_order,
                    grid_row:
                        pref.grid_row !== undefined
                            ? pref.grid_row
                            : defn.user_preference?.grid_row,
                    grid_col:
                        pref.grid_col !== undefined
                            ? pref.grid_col
                            : defn.user_preference?.grid_col,
                },
            };
        });
    },
    applyUserPreference(state, { id, payload }) {
        state.definitions = (state.definitions || []).map((defn) => {
            if (defn.id !== id) {
                return defn;
            }
            return {
                ...defn,
                user_preference: {
                    ...(defn.user_preference || {}),
                    ...payload,
                },
            };
        });
    },
};

const getters = {
    canManageKpis(state, getters, rootState, rootGetters) {
        const perms = rootGetters["session/current_user_permissions"] || [];
        return perms.indexOf("core.manage_homekpi") !== -1;
    },
    isAtLimit(state) {
        return (state.definitions || []).length >= 20;
    },
    visibleDefinitions(state, getters, rootState, rootGetters) {
        const scopes = rootGetters["session/current_user_scopes"] || [];
        const isOwner = rootGetters["session/current_user_is_owner"];
        const isCustomer = rootState.session?.entity?.profile === "customer";
        const isOperator = rootGetters["session/current_entity_profile"] === "operator"
            || rootGetters["session/current_entity_persona"] === "operator";
        return (state.definitions || []).filter((defn) => {
            if (defn.user_preference?.visible === false) {
                return false;
            }
            return canViewKpiType(defn.kpi_type, scopes, { isCustomer, isOwner, isOperator });
        });
    },
    sortedVisibleDefinitions(state, getters) {
        return [...getters.visibleDefinitions].sort((a, b) => {
            const prefA = a.user_preference || {};
            const prefB = b.user_preference || {};
            const rowA = prefA.grid_row ?? 0;
            const rowB = prefB.grid_row ?? 0;
            if (rowA !== rowB) {
                return rowA - rowB;
            }
            const colA = prefA.grid_col ?? prefA.sort_order ?? 0;
            const colB = prefB.grid_col ?? prefB.sort_order ?? 0;
            if (colA !== colB) {
                return colA - colB;
            }
            return new Date(a.created_at) - new Date(b.created_at);
        });
    },
};

const actions = {
    async fetchDefinitions({ commit }, { silent = false } = {}) {
        if (!silent) {
            commit("updateDefinitionsLoading", true);
        }
        commit("updateDefinitionsErrors", null);
        try {
            const response = await axios.get("/api/home-kpis/");
            const results = response.data?.results || response.data || [];
            commit("updateDefinitions", results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateDefinitionsErrors", error.details);
            throw error;
        } finally {
            if (!silent) {
                commit("updateDefinitionsLoading", false);
            }
        }
    },

    async fetchAllValues({ dispatch, getters }) {
        const defs = getters.sortedVisibleDefinitions;
        await Promise.all(defs.map((defn) => dispatch("fetchValue", defn.id)));
    },

    async fetchValue({ commit, state }, kpiId) {
        const defn = state.definitions.find((d) => d.id === kpiId);
        if (!defn) {
            return;
        }
        const url = HOME_KPI_STATS_URL[defn.kpi_type];
        if (!url) {
            return;
        }
        commit("updateValue", { id: kpiId, loading: true });
        try {
            const response = await axios.get(url, {
                params: buildStatsParams(defn.filters),
            });
            commit("updateValue", { id: kpiId, data: response.data, loading: false, error: null });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateValue", { id: kpiId, loading: false, error: error.details });
            throw error;
        }
    },

    async fetchPreview({ commit, state }, { kpiType, filters, metricKey }) {
        const url = HOME_KPI_STATS_URL[kpiType];
        if (!url) {
            return;
        }
        if (state.preview_cancel_source) {
            state.preview_cancel_source.cancel("canceled");
        }
        const cancelSource = axios.CancelToken.source();
        commit("updatePreviewCancelSource", cancelSource);
        commit("updatePreview", { loading: true, error: null });
        try {
            const response = await axios.get(url, {
                params: buildStatsParams(filters),
                cancelToken: cancelSource.token,
            });
            commit("updatePreview", { loading: false, data: response.data, error: null });
            commit("updatePreviewCancelSource", null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updatePreview", { loading: false, error: error.details });
            commit("updatePreviewCancelSource", null);
            throw error;
        }
    },

    async createDefinition({ commit, dispatch }, payload) {
        commit("updateSaving", true);
        try {
            const response = await axios.post("/api/home-kpis/", payload);
            await dispatch("fetchDefinitions");
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSaving", false);
        }
    },

    async updateDefinition({ commit, dispatch }, { id, payload }) {
        commit("updateSaving", true);
        try {
            const response = await axios.patch(`/api/home-kpis/${id}/`, payload);
            await dispatch("fetchDefinitions");
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSaving", false);
        }
    },

    async deleteDefinition({ commit, dispatch }, id) {
        commit("updateSaving", true);
        try {
            await axios.delete(`/api/home-kpis/${id}/`);
            await dispatch("fetchDefinitions");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSaving", false);
        }
    },

    async duplicateDefinition({ commit, dispatch }, id) {
        commit("updateSaving", true);
        try {
            const response = await axios.post(`/api/home-kpis/${id}/duplicate/`);
            await dispatch("fetchDefinitions");
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSaving", false);
        }
    },

    async savePreferencesBulk({ commit }, preferences) {
        commit("updatePreferencesSaving", true);
        try {
            await axios.put("/api/home-kpis/preferences/", { preferences });
            commit("applyUserPreferences", preferences);
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePreferencesSaving", false);
        }
    },

    async patchPreference({ commit }, { id, payload }) {
        try {
            await axios.patch(`/api/home-kpis/${id}/preferences/`, payload);
            commit("applyUserPreference", { id, payload });
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
